import { useTranslation } from '@client-app/i18n';
import { useAdminContext } from '@/contexts/AdminContext';
import {
    getMemberAvatarUrl,
    Permission, useAuth
} from '@/contexts/AuthContext';
import type { WorkspaceRole } from '@/contexts/AuthContext/useWorkspaces';
import { useToast } from '@/contexts/ToastContext';
import Skeleton from '@common/components/Skeleton';
import { Tooltip } from '@common/index';
import {
    featureService,
    type WorkspaceDataRole,
} from '@common/api/featureService';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
    ArrowRight,
    CaretDown,
    Check,
    CircleNotch,
    MagnifyingGlass,
    Plus,
    Trash,
    User,
    UserCirclePlus,
    Users,
    UsersThree,
    XCircle
} from '@phosphor-icons/react';
import {
    getMemberAssignmentRoleOptions,
    isOwnerRoleOption,
} from './roleAccessGuards';
import {
    applyWorkspaceSystemPermissionDetailsToRole,
    buildWorkspaceSystemPermissionGrantInputs,
    mapWorkspaceDataAccessPartsToRoles,
} from './workspaceDataAccessAdapter';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    hasHigherSelfServiceMemberTier,
    isMemberLimitReached,
    isSoloInviteUpgradeRequired,
    resolveEffectiveInviteMemberLimit,
    resolveInvitePlanName,
    resolveInvitePlanCode,
} from './memberInviteAccess';
import {
    type MemberInviteUpgradePrompt,
    getMemberInviteLimitPrompt,
    getMemberInviteUpgradePrompt,
    getMemberRolesUpgradePrompt,
} from './memberInviteUpgradePrompt';
import Modal from './Modal';
import { formatRoleLabel } from './roleLabels';
import { type Role } from './rolesData';
import RolesTab, { EMPTY_ROLE, RoleDrawer } from './RolesTab';
import { useSharedRoles } from './useSharedRoles';
import PaginationFooter, { getVisiblePaginationPages } from '@/components/Pagination/PaginationFooter';
import ContactSalesModal from '../../Pricing/ContactSalesModal';


// --- Helper Hook for Toast Adapter ---
const useToastAdapter = () => {
    const { showToast, showActionToast } = useToast();
    return {
        addToast: ({
            title, description, type, duration
        }: {
            title: string;
            description: string;
            type: 'success' | 'error' | 'info' | 'warning';
            duration?: number;
        }) => {
            if (duration !== undefined) {
                showActionToast({ title, description, type, duration });
                return;
            }
            showToast(`${title}: ${description}`, type);
        }
    };
};

const SYSTEM_WORKSPACE_ROLES: WorkspaceRole[] = ['OWNER', 'ADMIN', 'MEMBER'];
const DATA_ROLES_CACHE_TTL_MS = 15_000;
const MEMBERS_ITEMS_PER_PAGE = 10;
const MEMBER_INVITATION_SUCCESS_TOAST_DURATION_MS = 8000;

const formatIntegerForDisplay = (value: number, locale: string = 'en') => {
    const normalized = Number.isFinite(value) ? Math.trunc(value) : 0;
    return new Intl.NumberFormat(locale).format(Math.max(0, normalized));
};

const normalizeRoleValue = (role: string) => role.trim().toUpperCase();
const isWorkspaceMemberLimitError = (message?: string | null) => {
    const normalizedMessage = String(message || '').toLowerCase();
    return normalizedMessage.includes('includes up to') && normalizedMessage.includes('member');
};

const normalizeIdentityValue = (value: unknown) => String(value ?? '').trim().toLowerCase();

const getFirstIdentityValue = (...values: unknown[]) => {
    for (const value of values) {
        const normalizedValue = normalizeIdentityValue(value);
        if (normalizedValue) {
            return normalizedValue;
        }
    }
    return '';
};

const isSameIdentity = (left: unknown, right: unknown) => {
    const normalizedLeft = normalizeIdentityValue(left);
    const normalizedRight = normalizeIdentityValue(right);
    return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight);
};

const isConnectionOwnedByMember = (
    connectionRecord: Record<string, any>,
    member: Record<string, any>
) => {
    const memberId = getFirstIdentityValue(member.userId, member.id);
    const memberEmail = getFirstIdentityValue(member.email);
    const memberName = getFirstIdentityValue(member.displayName, member.name);

    const ownerId = getFirstIdentityValue(
        connectionRecord.user_id,
        connectionRecord.connected_by_user_id,
        connectionRecord.created_by_user_id,
        connectionRecord.connectedByUserId,
        connectionRecord.createdByUserId,
        connectionRecord.connectedBy
    );
    const ownerEmail = getFirstIdentityValue(
        connectionRecord.connected_by_email,
        connectionRecord.created_by_email,
        connectionRecord.connectedByEmail,
        connectionRecord.createdByEmail,
        connectionRecord.email
    );
    const ownerName = getFirstIdentityValue(
        connectionRecord.connected_by_name,
        connectionRecord.created_by_name,
        connectionRecord.connectedByName,
        connectionRecord.createdByName
    );

    return isSameIdentity(memberId, ownerId)
        || isSameIdentity(memberEmail, ownerEmail)
        || isSameIdentity(memberName, ownerName)
        || isSameIdentity(memberEmail, ownerId);
};

const getMemberOwnedConnectionCount = (
    member: Record<string, any> | null,
    integrations: Array<Record<string, any>>
) => {
    if (!member) {
        return 0;
    }

    return integrations.reduce((count, integration) => {
        const mappings = Array.isArray(integration.mappings) && integration.mappings.length > 0
            ? integration.mappings
            : integration.connected
                ? [integration]
                : [];

        return count + mappings.filter((mapping: Record<string, any>) => (
            isConnectionOwnedByMember({
                ...integration,
                ...mapping,
            }, member)
        )).length;
    }, 0);
};

const isWorkspaceRole = (role: string): role is WorkspaceRole =>
    SYSTEM_WORKSPACE_ROLES.includes(normalizeRoleValue(role) as WorkspaceRole);

type RoleOption = Pick<Role, 'id' | 'name' | 'description' | 'isSystem' | 'roleKey'>;
type RoleFilterOption = {
    value: string;
    label: string;
};
type DataRolesCacheEntry = {
    workspaceId: string;
    data: WorkspaceDataRole[];
    timestamp: number;
};
type TransferOwnershipTarget = {
    userId: string;
    displayName: string;
    email: string;
    role: string;
};

const mapWorkspaceDataRolesToRoleOptions = (workspaceRoles: WorkspaceDataRole[]): RoleOption[] =>
    workspaceRoles
        .filter(role => role.status !== 'INACTIVE')
        .map(role => ({
            id: String(role.id),
            name: role.name,
            description: role.description || '',
            roleKey: role.roleKey,
            isSystem: Boolean(role.systemRole) || role.roleType === 'SYSTEM_DEFAULT' || isWorkspaceRole(role.roleKey || role.name),
        }));

const resolveRoleAssignmentValue = (role: RoleOption) =>
    String(role.id);

const getRoleOptionValue = (role: RoleOption) =>
    String(role.id);

const findRoleOptionByValue = (roles: RoleOption[], value: string) =>
    roles.find(role =>
        getRoleOptionValue(role) === value
        || normalizeRoleValue(role.name) === normalizeRoleValue(value));

const buildOrderedRoleOptions = (roles: RoleOption[]) =>
    buildPrioritizedRoleOptions(roles, ['owner', 'admin', 'billing', 'member']);

const buildPrioritizedRoleOptions = (roles: RoleOption[], prioritizedRoleIds: string[]) => {
    const priorityMap = new Map(prioritizedRoleIds.map((roleId, index) => [normalizeRoleValue(roleId), index]));
    const prioritizedIdSet = new Set(prioritizedRoleIds);
    const prioritizedRoles = roles
        .filter(role => prioritizedIdSet.has(role.id) || priorityMap.has(normalizeRoleValue(role.name)))
        .sort((a, b) => (
            priorityMap.get(normalizeRoleValue(a.id)) ?? priorityMap.get(normalizeRoleValue(a.name)) ?? 99
        ) - (
            priorityMap.get(normalizeRoleValue(b.id)) ?? priorityMap.get(normalizeRoleValue(b.name)) ?? 99
        ));
    const remainingRoles = roles
        .filter(role => !prioritizedIdSet.has(role.id) && !priorityMap.has(normalizeRoleValue(role.name)))
        .sort((a, b) => a.name.localeCompare(b.name));

    return [...prioritizedRoles, ...remainingRoles];
};

const formatRoleFilterSummary = (selectedValues: string[], options: RoleFilterOption[]) => {
    if (selectedValues.length === 0) {
        return 'ALL_ROLES';
    }

    const selectedSet = new Set(selectedValues.map(normalizeRoleValue));
    const selectedLabels = options
        .filter(option => selectedSet.has(normalizeRoleValue(option.value)))
        .map(option => option.label);

    if (selectedLabels.length <= 1) {
        return selectedLabels[0] || 'ALL_ROLES';
    }

    return `${selectedLabels[0]}, +${selectedLabels.length - 1}`;
};

const RoleDropdownContent = ({
    roles,
    selectedValue,
    isLoading,
    onSelect,
    onNewRole,
}: {
    roles: RoleOption[];
    selectedValue: string;
    isLoading: boolean;
    onSelect: (role: RoleOption) => void;
    onNewRole?: () => void;
}) => {
    const { t } = useTranslation();

    return (
    <DropdownMenu.Portal>
        <DropdownMenu.Content
            className="z-[10002] overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
            sideOffset={6}
            align="start"
            collisionPadding={8}
            style={{
                width: 'var(--radix-dropdown-menu-trigger-width)',
                minWidth: 220,
                maxWidth: 'calc(100vw - 16px)',
            }}
        >
            <div className="px-3.5 pt-3 pb-1 text-xs font-semibold text-[#8A93B2]">
                {t('website.membersSettings.selectRole')}
            </div>
            <div className="max-h-[280px] overflow-y-auto p-1.5 space-y-0.5">
                {isLoading ? (
                    <div className="space-y-0.5">
                        <div className="flex items-center rounded-lg px-2 py-2.5">
                            <Skeleton width="110px" height="16px" className="rounded" />
                        </div>
                        <div className="flex items-center rounded-lg px-2 py-2.5">
                            <Skeleton width="75px" height="16px" className="rounded" />
                        </div>
                        <div className="flex items-center rounded-lg px-2 py-2.5">
                            <Skeleton width="95px" height="16px" className="rounded" />
                        </div>
                    </div>
                ) : roles.map((role) => {
                    const isSelected = getRoleOptionValue(role) === selectedValue
                        || normalizeRoleValue(selectedValue) === normalizeRoleValue(role.name);

                    return (
                        <DropdownMenu.Item
                            key={role.id}
                            onSelect={() => onSelect(role)}
                            className={clsx(
                                "flex items-center justify-between gap-3 rounded-lg px-2 py-2.5 text-left text-sm outline-none transition-colors",
                                isSelected ? 'bg-[#1A162F]/5' : 'hover:bg-gray-50'
                            )}
                        >
                    <span
                                className={clsx("font-medium truncate min-w-0", isSelected ? 'text-[#1A162F]' : 'text-text-main')}
                                title={formatRoleLabel(role.name, t)}
                            >
                                {formatRoleLabel(role.name, t)}
                            </span>
                            {isSelected ? <Check size={14} weight="regular" className="shrink-0 text-[#1A162F]" /> : null}
                        </DropdownMenu.Item>
                    );
                })}
            </div>
            {onNewRole && (
                <>
                    <div className="border-t border-[#ECEFF5] mx-1.5" />
                    <div className="p-1.5">
                        <DropdownMenu.Item
                            onSelect={onNewRole}
                            className="flex items-center gap-2 rounded-[10px] px-2.5 py-2.5 text-sm font-medium text-[#666F8D] outline-none transition-colors hover:bg-[#F8F9FC] hover:text-[#1A162F]"
                        >
                            <Plus size={14} weight="regular" />
                            {t('website.membersSettings.newRole')}
                        </DropdownMenu.Item>
                    </div>
                </>
            )}
        </DropdownMenu.Content>
    </DropdownMenu.Portal>
    );
};

// --- Components ---

// 表格行骨架屏组件
const MemberRowSkeleton = () => (
    <tr>
        <td className="px-4 py-3">
            <div className="flex items-center gap-3">
                <Skeleton width="36px" height="36px" className="rounded-full" />
                <div className="min-w-0 max-w-[200px] flex flex-col gap-2">
                    <Skeleton width="120px" height="16px" />
                    <Skeleton width="160px" height="14px" />
                </div>
            </div>
        </td>
        <td className="px-4 py-3">
            <Skeleton width="80px" height="20px" />
        </td>
        <td className="px-4 py-3">
            <Skeleton width="70px" height="24px" className="rounded-full" />
        </td>
        <td className="px-4 py-3">
            <Skeleton width="100px" height="16px" />
        </td>
        <td className="px-4 py-3 text-right">
            <Skeleton width="32px" height="32px" className="inline-block" />
        </td>
    </tr>
);

const RoleSelect = ({
    member,
    onUpdateRole,
    canChangeRole,
    roles,
    allowCustomRoles,
    isUpdating,
    isLoadingRoles,
    onNewRole,
}: {
    member: {
        role: WorkspaceRole | string;
        userId?: string;
        id?: string;
        isCurrentUser?: boolean;
    };
        onUpdateRole: (role: RoleOption) => void;
    canChangeRole: boolean;
    roles: RoleOption[];
    allowCustomRoles: boolean;
    isUpdating: boolean;
    isLoadingRoles: boolean;
    onNewRole?: () => void;
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const displayedRole = normalizeRoleValue(String(member.role || ''));

    const canEdit = (() => {
        if (!canChangeRole) return false;
        if (member.isCurrentUser) return false;
        if (displayedRole === 'OWNER') return false;
        return true;
    })();

    const displayRole = formatRoleLabel(member.role, t);

    if (!canEdit) {
        return (
            <span
                className="inline-flex items-center gap-1.5 text-sm font-medium text-text-main max-w-[170px] truncate"
                title={displayRole}
            >
                {displayRole}
            </span>
        );
    }

    const roleOptions = getMemberAssignmentRoleOptions(roles, allowCustomRoles)
        .filter((role, index, allRoles) => allRoles.findIndex(candidate => candidate.id === role.id) === index)
        .filter(role => !isOwnerRoleOption(role));
    const orderedRoleOptions = buildPrioritizedRoleOptions(roleOptions, ['admin', 'member']);

    return (
        <DropdownMenu.Root
            open={isOpen}
            onOpenChange={(nextOpen) => {
                if (isUpdating) {
                    return;
                }
                setIsOpen(nextOpen);
            }}
        >
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    disabled={isUpdating}
                    className={clsx(
                        "inline-flex items-center justify-start gap-1.5 rounded-lg border border-transparent pl-0 pr-2.5 py-1.5 text-sm font-medium text-text-main transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 max-w-[170px] min-w-0",
                        isUpdating && "cursor-wait opacity-80"
                    )}
                >
                    <span className="truncate" title={displayRole}>{displayRole}</span>
                    <CaretDown
                        size={12}
                        weight="regular"
                        className={clsx("opacity-50 transition-transform duration-200 shrink-0", isOpen && "rotate-180")}
                    />
                </button>
            </DropdownMenu.Trigger>
            <RoleDropdownContent
                roles={orderedRoleOptions}
                selectedValue={member.role}
                isLoading={isLoadingRoles}
                onSelect={(selectedRole) => {
                    onUpdateRole(selectedRole);
                    setIsOpen(false);
                }}
                onNewRole={onNewRole ? () => {
                    setIsOpen(false);
                    onNewRole();
                } : undefined}
            />
        </DropdownMenu.Root>
    );
};


const MemberActions = ({
    member,
    onRemove,
    canRemove,
    isDisabled,
}: {
    member: {
        role: WorkspaceRole | string;
        isCurrentUser?: boolean;
    };
    onRemove: () => void;
    canRemove: boolean;
    isDisabled?: boolean;
}) => {
    const { t } = useTranslation();
    const displayedRole = normalizeRoleValue(String(member.role || ''));

    if (!canRemove) return null;
    if (member.isCurrentUser) return null;
    if (displayedRole === 'OWNER') return null;

    return (
        <Tooltip content={t('website.membersSettings.removeUser')} side="top">
            <button
                type="button"
                onClick={onRemove}
                disabled={isDisabled}
                aria-label={t('website.membersSettings.removeUser')}
                className={clsx(
                    "p-1.5 rounded text-text-sub transition-colors",
                    isDisabled
                        ? "cursor-not-allowed opacity-50"
                        : "hover:bg-red-50 hover:text-red-600"
                )}
            >
                <Trash size={20} weight="regular" />
            </button>
        </Tooltip>
    );
};

const PendingInviteActions = ({
    onResend,
    onCancel,
    canInvite,
    isResending,
    isCancelling,
}: {
    onResend: () => void;
    onCancel: () => void;
    canInvite: boolean;
    isResending?: boolean;
    isCancelling?: boolean;
}) => {
    const { t } = useTranslation();
    const resendLabel = t('website.membersSettings.resendInvite');
    const resendingLabel = t('website.membersSettings.resendingInvite');
    const cancelLabel = t('website.membersSettings.cancelInvite');
    const cancellingLabel = t('website.membersSettings.cancellingInvite');
    const resendButtonLabel = isResending ? resendingLabel : resendLabel;
    const cancelButtonLabel = isCancelling ? cancellingLabel : cancelLabel;
    const isBusy = Boolean(isResending || isCancelling);

    if (!canInvite) return null;

    return (
        <div className="inline-flex items-center justify-end gap-2">
            <Tooltip content={resendButtonLabel} side="top">
                <button
                    type="button"
                    onClick={onResend}
                    disabled={isBusy}
                    aria-label={resendButtonLabel}
                    aria-busy={isResending}
                    className={clsx(
                        "inline-flex min-w-[84px] items-center justify-center rounded-[8px] border border-[#E8EBF3] bg-white px-2.5 py-1.5 text-xs font-medium text-[#1A162F] transition-colors",
                        isBusy
                            ? "cursor-not-allowed opacity-60"
                            : "hover:border-[#C8D0E2] hover:bg-[#F8F9FC]"
                    )}
                >
                    {isResending ? (
                        <>
                            <span
                                aria-hidden="true"
                                className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#666F8D] border-t-transparent"
                            />
                            <span className="sr-only">{resendButtonLabel}</span>
                        </>
                    ) : resendButtonLabel}
                </button>
            </Tooltip>
            <Tooltip content={cancelButtonLabel} side="top">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isBusy}
                    aria-label={cancelButtonLabel}
                    aria-busy={isCancelling}
                    className={clsx(
                        "inline-flex min-w-[74px] items-center justify-center rounded-[8px] border border-[#F1D3D3] bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors",
                        isBusy
                            ? "cursor-not-allowed opacity-60"
                            : "hover:border-red-200 hover:bg-red-50"
                    )}
                >
                    {isCancelling ? (
                        <>
                            <span
                                aria-hidden="true"
                                className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-red-500 border-t-transparent"
                            />
                            <span className="sr-only">{cancelButtonLabel}</span>
                        </>
                    ) : cancelButtonLabel}
                </button>
            </Tooltip>
        </div>
    );
};

const TeamUpgradeModal = ({
    isOpen,
    onClose,
    onPrimaryAction,
    prompt,
}: {
    isOpen: boolean;
    onClose: () => void;
    onPrimaryAction: () => void;
    prompt: MemberInviteUpgradePrompt;
}) => (
    <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={prompt.title}
        width="max-w-[500px]"
        containerClassName="overflow-hidden rounded-2xl border border-white/70 shadow-[0_24px_60px_rgba(26,22,47,0.18)]"
        headerClassName="px-6 py-5"
        bodyClassName="px-6 pb-6 pt-4"
        showHeaderDivider
    >
        <div>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-[18px] border border-[#E8EBF3] bg-[#F4F5F8] text-[#1A162F] shadow-[0_8px_20px_rgba(26,22,47,0.08)]">
                <Users size={28} weight="regular" />
            </div>

            <div className="mx-auto max-w-[370px] text-center">
                <p className="mx-auto text-sm font-normal leading-6 text-[#666F8D]">
                    {prompt.description}
                </p>
            </div>

            <div className="mt-6 flex items-center justify-center gap-3">
                <button
                    onClick={onClose}
                    className="inline-flex items-center justify-center rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC]"
                >
                    {prompt.secondaryActionLabel}
                </button>
                <button
                    onClick={onPrimaryAction}
                    className="inline-flex items-center gap-2 rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-[#231D3E]"
                >
                    {prompt.primaryActionLabel}
                    <ArrowRight size={16} weight="regular" />
                </button>
            </div>
        </div>
    </Modal>
);

// --- Invite Role Dropdown ---
const InviteRoleDropdown = ({
    value, onChange, roles, disabled, onNewRole
}: {
    value: string;
    onChange: (role: string) => void;
    roles: RoleOption[];
    disabled?: boolean;
    onNewRole?: () => void;
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const selected = findRoleOptionByValue(roles, value) || roles[0];
    const orderedRoles = buildPrioritizedRoleOptions(roles, ['admin', 'member']);

    return (
        <DropdownMenu.Root open={disabled ? false : open} onOpenChange={disabled ? undefined : setOpen}>
            <DropdownMenu.Trigger asChild disabled={disabled}>
                <button
                    type="button"
                    disabled={disabled}
                    className={clsx(
                        "flex min-w-0 w-full items-center justify-between rounded-[10px] border bg-white px-3.5 py-3 text-left text-sm transition-all disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-50",
                        open
                            ? "border-[#1A162F] bg-[#F8F9FC] ring-2 ring-[#1A162F]/20"
                            : "border-[#E8EBF3] hover:border-[#C8D0E2]"
                    )}
                >
                    <div
                        className="truncate pr-2 font-normal text-[#1A162F]"
                        title={selected ? formatRoleLabel(selected.name, t) : t('website.membersSettings.noRolesAvailable')}
                    >
                        {selected ? formatRoleLabel(selected.name, t) : t('website.membersSettings.noRolesAvailable')}
                    </div>
                    <CaretDown
                        size={14}
                        weight="regular"
                        className={clsx("shrink-0 text-[#8A93B2] transition-transform duration-200", open && "rotate-180")}
                    />
                </button>
            </DropdownMenu.Trigger>
            <RoleDropdownContent
                roles={orderedRoles}
                selectedValue={value}
                isLoading={false}
                onSelect={(role) => {
                    onChange(getRoleOptionValue(role));
                    setOpen(false);
                }}
                onNewRole={onNewRole ? () => {
                    setOpen(false);
                    onNewRole();
                } : undefined}
            />
        </DropdownMenu.Root>
    );
};

const RoleFilterDropdown = ({
    options,
    selectedValues,
    onToggle,
    onClear,
}: {
    options: RoleFilterOption[];
    selectedValues: string[];
    onToggle: (role: string) => void;
    onClear: () => void;
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const selectedSet = new Set(selectedValues.map(normalizeRoleValue));
    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(query.trim().toLowerCase()));
    const summaryKey = formatRoleFilterSummary(selectedValues, options);
    const summary = summaryKey === 'ALL_ROLES' ? t('website.membersSettings.allRoles') : summaryKey;

    return (
        <div className="flex items-center gap-2 min-w-[220px]">
            <DropdownMenu.Root
                open={open}
                onOpenChange={(nextOpen) => {
                    setOpen(nextOpen);
                    if (!nextOpen) {
                        setQuery('');
                    }
                }}
            >
                <DropdownMenu.Trigger asChild>
                    <button
                        type="button"
                        className={clsx(
                            "inline-flex min-w-[220px] items-center justify-between gap-3 rounded-[10px] border px-3 py-2.5 text-left text-sm transition-all",
                            selectedValues.length > 0
                                ? "border-[#C8D0E2] bg-[#F8F9FC] text-[#1A162F]"
                                : "border-[#E8EBF3] bg-white text-[#1A162F] hover:border-[#C8D0E2]",
                            open && "border-[#1A162F] ring-2 ring-[#1A162F]/20"
                        )}
                    >
                        <span className="truncate font-normal">{summary}</span>
                        <CaretDown
                            size={14}
                            weight="regular"
                            className={clsx("shrink-0 text-[#8A93B2] transition-transform duration-200", open && "rotate-180")}
                        />
                    </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        className="z-[10002] overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
                        sideOffset={8}
                        align="start"
                        collisionPadding={8}
                        style={{
                            width: 320,
                            maxWidth: 'calc(100vw - 16px)',
                        }}
                    >
                        <div className="border-b border-[#ECEFF5] p-2">
                            <div className="relative">
                                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93B2]" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    onKeyDown={(event) => event.stopPropagation()}
                                    placeholder={t('website.membersSettings.filterRoles')}
                                    className="w-full rounded-[10px] border border-[#E8EBF3] bg-[#FCFCFC] py-2.5 pl-9 pr-3 text-sm text-[#1A162F] focus:outline-none transition-all placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:ring-1 focus:ring-[#1A162F]"
                                />
                            </div>
                        </div>
                        <div className="max-h-[320px] overflow-y-auto p-1.5">
                            {filteredOptions.length > 0 ? (
                                <div className="space-y-0.5">
                                    {filteredOptions.map(option => {
                                        const isSelected = selectedSet.has(normalizeRoleValue(option.value));

                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => onToggle(option.value)}
                                                className={clsx(
                                                    "flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2.5 text-left transition-colors",
                                                    isSelected ? 'bg-[#F4F5F8] text-[#1A162F]' : 'text-text-main hover:bg-[#F8F9FC]'
                                                )}
                                            >
                                                <span className={clsx(
                                                    "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                                                    isSelected ? 'border-[#1A162F] bg-[#1A162F] text-white' : 'border-[#C8D0E2] bg-white text-transparent'
                                                )}>
                                                    <Check size={12} weight="bold" />
                                                </span>
                                                <span className="truncate text-sm font-medium">{option.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="px-3 py-6 text-center text-sm text-text-sub">
                                    {t('website.membersSettings.noRolesFound')}
                                </div>
                            )}
                        </div>
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
            {selectedValues.length > 0 ? (
                <button
                    type="button"
                    onClick={onClear}
                    className="text-sm font-normal text-[#666F8D] transition-colors hover:text-[#1A162F]"
                >
                    {t('website.membersSettings.clear')}
                </button>
            ) : null}
        </div>
    );
};

// --- Main Component ---

const MembersSettings = () => {
    const { locale, t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'members' | 'roles'>('members');
    const {
        workspaceMembers, permissions, userBilling, currentWorkspace, integrations
    } = useAuth();
    const [roles, setRoles] = useSharedRoles(currentWorkspace?.id);
    const { modal } = useAdminContext();
    const {
        members,
        pendingInvites,
        entitlements,
        status: membersStatus,
        pendingOwnerTransfer,
        error,
        refresh,
        inviteMember,
        cancelInvite,
        removeMember,
        transferOwnership,
    } = workspaceMembers;

    // 权限检查（后续会叠加 owner 转交后的本地角色限制）
    const canInviteBase = permissions.checkPermissions([Permission.MEMBER_INVITE]);
    const canRemoveBase = permissions.checkPermissions([Permission.MEMBER_REMOVE]);
    const canChangeRoleBase = permissions.checkPermissions([Permission.MEMBER_CHANGE_ROLE]);
    const workspacePlanCode = resolveInvitePlanCode({
        currentWorkspacePlanCode: currentWorkspace?.subscription?.plan_code,
        billingSummaryPlanCode: userBilling.summary?.planCode,
        userPlanCode: userBilling.userPlan?.planCode,
    });
    const isSoloWorkspace = isSoloInviteUpgradeRequired(workspacePlanCode);
    const requiresInviteUpgrade = canInviteBase && isSoloWorkspace;
    const effectiveInviteMemberLimit = resolveEffectiveInviteMemberLimit({
        planCode: workspacePlanCode,
        memberLimit: entitlements.memberLimit,
    });
    const invitePlanName = resolveInvitePlanName(workspacePlanCode);
    const inviteLimitPrimaryActionTarget = hasHigherSelfServiceMemberTier(workspacePlanCode)
        ? 'plans'
        : 'sales';
    const hasReachedMemberLimit = canInviteBase && isMemberLimitReached({
        memberCount: members.length,
        memberLimit: effectiveInviteMemberLimit,
    });

    const { addToast } = useToastAdapter();
    const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
    const [isRoleOptionsLoading, setIsRoleOptionsLoading] = useState(false);
    const [isRolesTabLoading, setIsRolesTabLoading] = useState(false);
    const rolesTabRequestIdRef = useRef(0);
    const roleOptionsRequestIdRef = useRef(0);
    const dataRolesRequestRef = useRef<Promise<WorkspaceDataRole[]> | null>(null);
    const dataRolesRequestVersionRef = useRef(0);
    const dataRolesCacheRef = useRef<DataRolesCacheEntry | null>(null);
    const inlineRoleDrawerRequestIdRef = useRef(0);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRoleFilters, setSelectedRoleFilters] = useState<string[]>([]);
    const [membersPage, setMembersPage] = useState(1);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<string>('MEMBER');
    const [memberToDelete, setMemberToDelete] = useState<any>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isTransferOwnershipModalOpen, setIsTransferOwnershipModalOpen] = useState(false);
    const [transferTargetMemberId, setTransferTargetMemberId] = useState('');
    const [transferTargetSnapshot, setTransferTargetSnapshot] = useState<TransferOwnershipTarget | null>(null);
    const [isTransferringOwnership, setIsTransferringOwnership] = useState(false);
    const [resendingInviteIds, setResendingInviteIds] = useState<Set<string>>(new Set());
    const [cancellingInviteIds, setCancellingInviteIds] = useState<Set<string>>(new Set());
    const [updatingRoleMemberIds, setUpdatingRoleMemberIds] = useState<Set<string>>(new Set());
    const [optimisticRoleByMemberId, setOptimisticRoleByMemberId] = useState<Record<string, string>>({});
    const [
        confirmedDataRoleByMemberId,
        setConfirmedDataRoleByMemberId,
    ] = useState<Record<string, string>>({});
    const [emailError, setEmailError] = useState('');
    const [isInviteUpgradeModalOpen, setIsInviteUpgradeModalOpen] = useState(false);
    const [isRolesUpgradeModalOpen, setIsRolesUpgradeModalOpen] = useState(false);
    const [isInviteLimitModalOpen, setIsInviteLimitModalOpen] = useState(false);
    const [isInviteLimitContactSalesOpen, setIsInviteLimitContactSalesOpen] = useState(false);
    const [inlineEditingRole, setInlineEditingRole] = useState<Role | undefined>(undefined);
    const [isInlineRolePreparing, setIsInlineRolePreparing] = useState(false);
    const inviteUpgradePrompt = getMemberInviteUpgradePrompt(t);
    const ownedConnectionCountForDelete = useMemo(
        () => getMemberOwnedConnectionCount(
            memberToDelete,
            integrations.allIntegrations as Array<Record<string, any>>
        ),
        [integrations.allIntegrations, memberToDelete]
    );
    const rolesUpgradePrompt = getMemberRolesUpgradePrompt(t);
    const inviteLimitPrompt = getMemberInviteLimitPrompt(t, {
        planName: invitePlanName,
        memberLimit: effectiveInviteMemberLimit,
        primaryActionTarget: inviteLimitPrimaryActionTarget,
    });
    const canInvite = canInviteBase;
    const canRemove = canRemoveBase;
    const canChangeRole = canChangeRoleBase;
    const canManageMembers = canInvite || canRemove || canChangeRole;
    const canManageRoleAccess = canChangeRoleBase;
    const canTransferOwnership = permissions.canTransferOwnership && !isSoloWorkspace;
    const canStartOwnerTransfer = canTransferOwnership && !pendingOwnerTransfer;
    const canLoadMemberRoleOptions = Boolean(currentWorkspace?.id) && !isSoloWorkspace;
    const inlineEmptyRole = useMemo<Role>(() => ({
        ...EMPTY_ROLE,
        permissions: {
            ...EMPTY_ROLE.permissions,
            connections: [],
        },
    }), []);

    useEffect(() => {
        rolesTabRequestIdRef.current += 1;
        roleOptionsRequestIdRef.current += 1;
        dataRolesRequestRef.current = null;
        dataRolesRequestVersionRef.current += 1;
        dataRolesCacheRef.current = null;
    }, [currentWorkspace?.id]);

    const fetchWorkspaceDataRoles = useCallback((forceRefresh = false) => {
        if (!currentWorkspace?.id) {
            return Promise.resolve([] as WorkspaceDataRole[]);
        }

        const cachedDataRoles = dataRolesCacheRef.current;
        if (
            !forceRefresh
            && cachedDataRoles?.workspaceId === currentWorkspace.id
            && Date.now() - cachedDataRoles.timestamp < DATA_ROLES_CACHE_TTL_MS
        ) {
            return Promise.resolve(cachedDataRoles.data);
        }

        if (forceRefresh || !dataRolesRequestRef.current) {
            const requestVersion = dataRolesRequestVersionRef.current + 1;
            dataRolesRequestVersionRef.current = requestVersion;
            dataRolesRequestRef.current = featureService.getWorkspaceDataRoles(currentWorkspace.id)
                .then(dataRoles => {
                    if (dataRolesRequestVersionRef.current === requestVersion) {
                        dataRolesCacheRef.current = {
                            workspaceId: currentWorkspace.id,
                            data: dataRoles,
                            timestamp: Date.now(),
                        };
                    }
                    return dataRoles;
                })
                .catch(error => {
                    throw error;
                })
                .finally(() => {
                    if (dataRolesRequestVersionRef.current === requestVersion) {
                        dataRolesRequestRef.current = null;
                    }
                });
        }

        return dataRolesRequestRef.current;
    }, [currentWorkspace?.id]);

    const loadWorkspaceDataRoles = useCallback(async (forceRefresh = false) => {
        if (!currentWorkspace?.id || isSoloWorkspace) {
            setRoles([]);
            setIsRolesTabLoading(false);
            return [];
        }

        const requestId = rolesTabRequestIdRef.current + 1;
        rolesTabRequestIdRef.current = requestId;
        setIsRolesTabLoading(true);
        try {
            const dataRoles = await fetchWorkspaceDataRoles(forceRefresh);
            if (rolesTabRequestIdRef.current !== requestId) {
                return dataRoles;
            }
            setRoles(mapWorkspaceDataAccessPartsToRoles({
                workspaceId: currentWorkspace.id,
                roles: dataRoles,
            }, t));
            return dataRoles;
        } catch (error) {
            console.error('Failed to load workspace data roles:', error);
            return [];
        } finally {
            if (rolesTabRequestIdRef.current === requestId) {
                setIsRolesTabLoading(false);
            }
        }
    }, [currentWorkspace?.id, fetchWorkspaceDataRoles, isSoloWorkspace, setRoles, t]);

    const loadWorkspaceRoleOptions = useCallback(async (forceRefresh = false) => {
        if (!currentWorkspace?.id || isSoloWorkspace || !canLoadMemberRoleOptions) {
            setRoleOptions([]);
            setIsRoleOptionsLoading(false);
            return [];
        }

        const requestId = roleOptionsRequestIdRef.current + 1;
        roleOptionsRequestIdRef.current = requestId;
        setIsRoleOptionsLoading(true);
        try {
            const workspaceRoles = await fetchWorkspaceDataRoles(forceRefresh);
            if (roleOptionsRequestIdRef.current !== requestId) {
                return workspaceRoles;
            }
            const nextRoleOptions = mapWorkspaceDataRolesToRoleOptions(workspaceRoles || []);
            setRoleOptions(nextRoleOptions);
            return nextRoleOptions;
        } catch (error) {
            console.error('Failed to load workspace role options:', error);
            return [];
        } finally {
            if (roleOptionsRequestIdRef.current === requestId) {
                setIsRoleOptionsLoading(false);
            }
        }
    }, [canLoadMemberRoleOptions, currentWorkspace?.id, fetchWorkspaceDataRoles, isSoloWorkspace]);

    // 邮箱校验函数
    const validateEmail = (email: string) => {
        if (!email.trim()) {
            return t('website.membersSettings.emailRequired');
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return t('website.membersSettings.emailInvalid');
        }
        return '';
    };

    // 处理邮箱输入变化
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInviteEmail(e.target.value);
        // 输入时清除错误提示
        if (emailError) {
            setEmailError('');
        }
    };

    // Workspace 切换时重置本地 UI 选择态，由 workspaceMembers hook 负责数据加载。
    useEffect(() => {
        setOptimisticRoleByMemberId({});
        setConfirmedDataRoleByMemberId({});
        setUpdatingRoleMemberIds(new Set());
        setIsTransferOwnershipModalOpen(false);
        setTransferTargetMemberId('');
        setTransferTargetSnapshot(null);
        setSelectedRoleFilters([]);
        setMembersPage(1);
    }, [currentWorkspace?.id]);

    useEffect(() => {
        setMembersPage(1);
    }, [currentWorkspace?.id, searchQuery, selectedRoleFilters]);

    useEffect(() => {
        void loadWorkspaceRoleOptions();
    }, [loadWorkspaceRoleOptions]);

    useEffect(() => {
        if (activeTab === 'roles') {
            void loadWorkspaceDataRoles();
        }
    }, [activeTab, loadWorkspaceDataRoles]);

    useEffect(() => {
        if (isSoloWorkspace && activeTab === 'roles') {
            setActiveTab('members');
        }
    }, [activeTab, isSoloWorkspace]);

    const membersWithDataRoles = useMemo(() => members.map((member: any) => {
        const memberId = String(member.userId || member.id);
        const confirmedDataRole = confirmedDataRoleByMemberId[memberId];
        const assignedRoleName = confirmedDataRole || member.dataRoleName || member.data_role_name;
        const shouldShowDataRole = Boolean(assignedRoleName) && member.role !== 'OWNER';

        return {
            ...member,
            role: shouldShowDataRole ? assignedRoleName : member.role,
        };
    }), [confirmedDataRoleByMemberId, members]);

    // Filter members and pending invites by search query
    const allMembers = membersWithDataRoles;
    const getMemberRowId = (member: any) => String(member.userId || member.id);
    const resolveMemberRole = (member: any) => {
        const memberId = getMemberRowId(member);
        return optimisticRoleByMemberId[memberId] ?? member.role;
    };
    const roleAssignableMembers = allMembers.map((member: any) => {
        const userId = getMemberRowId(member);

        return {
            userId,
            displayName: member.displayName,
            email: member.email,
            role: String(resolveMemberRole(member) || ''),
            status: String(member.status || ''),
        };
    });
    const inviteRoleOptions = [
        ...roleOptions.filter(role => !isOwnerRoleOption(role)),
    ];
    const memberAssignmentRoleOptions = useMemo(() => [
        ...roleOptions.filter(role => !isOwnerRoleOption(role)),
    ], [roleOptions]);
    const roleFilterRoleNames = [
        ...allMembers.map((member: any) => String(resolveMemberRole(member) || '')),
        ...pendingInvites.map((invite: any) => String(invite.dataRoleName || invite.data_role_name || invite.role || '')),
    ].filter(Boolean);
    const roleFilterOptionByName = new Map<string, RoleOption>();
    [...roleOptions, ...roles].forEach((role) => {
        roleFilterOptionByName.set(normalizeRoleValue(role.name), {
            id: role.id,
            name: role.name,
            description: role.description,
            roleKey: role.roleKey,
            isSystem: role.isSystem,
        });
    });
    roleFilterRoleNames.forEach((roleName) => {
        const normalizedRoleName = normalizeRoleValue(roleName);
        if (!roleFilterOptionByName.has(normalizedRoleName)) {
            roleFilterOptionByName.set(normalizedRoleName, {
                id: normalizedRoleName,
                name: roleName,
                description: '',
                roleKey: isWorkspaceRole(roleName) ? normalizedRoleName : undefined,
                isSystem: isWorkspaceRole(roleName),
            });
        }
    });
    const roleFilterRoleOptions = Array.from(roleFilterOptionByName.values());
    const roleFilterOptions = buildOrderedRoleOptions(roleFilterRoleOptions).map(role => ({
        value: role.name,
        label: formatRoleLabel(role.name, t),
    }));
    const selectedRoleFilterSet = new Set(selectedRoleFilters.map(normalizeRoleValue));
    const matchesSelectedRoleFilters = (role?: string | null) =>
        selectedRoleFilterSet.size === 0
        || selectedRoleFilterSet.has(normalizeRoleValue(String(role || '')));
    const filteredMembers = allMembers.filter((m: any) =>
        (m.displayName.toLowerCase().includes(searchQuery.toLowerCase())
        || m.email?.toLowerCase().includes(searchQuery.toLowerCase()))
        && matchesSelectedRoleFilters(resolveMemberRole(m)))
        .sort((a: any, b: any) => {
            // 将当前用户放在第一位
            if (a.isCurrentUser && !b.isCurrentUser) return -1;
            if (!a.isCurrentUser && b.isCurrentUser) return 1;
            return 0;
        });

    const filteredPendingInvites = pendingInvites.filter((invite: any) =>
        invite.email.toLowerCase().includes(searchQuery.toLowerCase())
        && matchesSelectedRoleFilters(String(invite.role || '')));

    // Combine: members first, then pending invites
    const allRows = [
        ...filteredMembers.map((m: any) => ({
            ...m,
            role: resolveMemberRole(m),
            type: 'member' as const
        })),
        ...filteredPendingInvites.map((invite: any) => ({
            ...invite,
            role: invite.dataRoleName || invite.data_role_name || invite.role,
            workspaceRole: invite.role,
            type: 'pending' as const
        }))
    ];
    const totalMemberRows = allRows.length;
    const totalMemberPages = Math.max(1, Math.ceil(totalMemberRows / MEMBERS_ITEMS_PER_PAGE));
    const visibleMemberPages = useMemo(
        () => getVisiblePaginationPages(membersPage, totalMemberPages),
        [membersPage, totalMemberPages],
    );
    const paginatedRows = allRows.slice(
        (membersPage - 1) * MEMBERS_ITEMS_PER_PAGE,
        membersPage * MEMBERS_ITEMS_PER_PAGE,
    );
    useEffect(() => {
        setMembersPage(page => Math.min(Math.max(1, page), totalMemberPages));
    }, [totalMemberPages]);
    const shouldShowInitialSkeleton = membersStatus === 'loading';
    const shouldShowLoadError = membersStatus === 'error';
    const rolePriorityMap: Record<string, number> = {
        ADMIN: 0,
        MEMBER: 1,
    };
    const transferCandidates = allMembers
        .filter((member: any) => {
            const userId = getMemberRowId(member);
            const status = String(member.status || '').toUpperCase();
            const role = normalizeRoleValue(String(resolveMemberRole(member) || ''));
            return status === 'ACTIVE' && !member.isCurrentUser && role !== 'OWNER';
        })
        .map((member: any): TransferOwnershipTarget => ({
            userId: getMemberRowId(member),
            displayName: member.displayName,
            email: member.email,
            role: String(resolveMemberRole(member) || ''),
        }))
        .sort((a, b) => {
            const aKey = normalizeRoleValue(a.role);
            const bKey = normalizeRoleValue(b.role);
            const aPriority = rolePriorityMap[aKey] ?? 99;
            const bPriority = rolePriorityMap[bKey] ?? 99;

            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            return a.displayName.localeCompare(b.displayName);
        });
    const selectedTransferCandidate = transferCandidates.find(member => member.userId === transferTargetMemberId) || null;
    const selectedTransferTarget = selectedTransferCandidate
        || (isTransferringOwnership && transferTargetSnapshot?.userId === transferTargetMemberId ? transferTargetSnapshot : null);
    const currentUserMember = allMembers.find((member: any) => member.isCurrentUser) || null;
    const canConfirmOwnershipTransfer = Boolean(selectedTransferTarget);

    const toggleRoleFilter = (roleValue: string) => {
        setSelectedRoleFilters(prev => {
            const normalizedRoleValue = normalizeRoleValue(roleValue);
            const hasRole = prev.some(value => normalizeRoleValue(value) === normalizedRoleValue);

            if (hasRole) {
                return prev.filter(value => normalizeRoleValue(value) !== normalizedRoleValue);
            }

            return [...prev, roleValue];
        });
    };

    const clearSearch = useCallback(() => {
        setSearchQuery('');
        searchInputRef.current?.focus();
    }, []);

    const handleInvite = async () => {
        // 点击发送时才进行校验
        const error = validateEmail(inviteEmail);
        if (error) {
            setEmailError(error);
            return;
        }

        const normalizedInviteEmail = inviteEmail.trim().toLowerCase();
        const isExistingMember = allMembers.some((member: any) =>
            String(member.email || '').toLowerCase() === normalizedInviteEmail);

        if (isExistingMember) {
            setEmailError(t('website.membersSettings.emailAlreadyMember'));
            return;
        }

        if (hasReachedMemberLimit) {
            setIsInviteModalOpen(false);
            setIsInviteLimitModalOpen(true);
            return;
        }

        setIsProcessing(true);
        try {
            const selectedInviteRole = findRoleOptionByValue(inviteRoleOptions, inviteRole);
            if (!selectedInviteRole) {
                throw new Error(t('website.membersSettings.selectedRoleNoLongerAvailable'));
            }

            const workspaceRole: WorkspaceRole = 'MEMBER';
            const dataRoleId = Number(selectedInviteRole.id);
            if (!Number.isFinite(dataRoleId)) {
                throw new Error(t('website.membersSettings.selectedAccessRoleInvalid'));
            }

            await inviteMember(inviteEmail, workspaceRole, dataRoleId);
            setIsInviteModalOpen(false);
            setInviteEmail('');
            setEmailError('');
            setInviteRole('MEMBER');
            addToast({
                title: t('website.membersSettings.invitationSent'),
                description: t('website.membersSettings.invitationSentDescription', {
                    email: inviteEmail,
                    workspace: currentWorkspace?.name,
                }),
                type: 'success',
                duration: MEMBER_INVITATION_SUCCESS_TOAST_DURATION_MS,
            });
        } catch (error: any) {
            if (isWorkspaceMemberLimitError(error.message)) {
                setIsInviteModalOpen(false);
                setIsInviteLimitModalOpen(true);
                return;
            }

            addToast({
                title: t('website.membersSettings.invitationFailed'),
                description: error.message || t('website.membersSettings.failedToInviteUser'),
                type: 'error'
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const resolvePendingInviteWorkspaceRole = (invite: any): WorkspaceRole => {
        const inviteWorkspaceRole = String(invite.workspaceRole || invite.membershipRole || 'MEMBER');
        return isWorkspaceRole(inviteWorkspaceRole)
            ? normalizeRoleValue(inviteWorkspaceRole) as WorkspaceRole
            : 'MEMBER';
    };

    const resolvePendingInviteDataRoleId = (invite: any) => {
        const rawDataRoleId = invite.dataRoleId ?? invite.data_role_id;
        if (rawDataRoleId !== undefined && rawDataRoleId !== null && rawDataRoleId !== '') {
            const dataRoleId = Number(rawDataRoleId);
            if (Number.isFinite(dataRoleId)) {
                return dataRoleId;
            }
        }

        const inviteRoleName = String(invite.dataRoleName || invite.data_role_name || invite.role || '');
        const matchedRole = inviteRoleOptions.find(role =>
            normalizeRoleValue(role.name) === normalizeRoleValue(inviteRoleName)
            || normalizeRoleValue(role.roleKey || '') === normalizeRoleValue(inviteRoleName));
        const matchedDataRoleId = Number(matchedRole?.id);

        return Number.isFinite(matchedDataRoleId) ? matchedDataRoleId : null;
    };

    const handleResendInvite = async (invite: any) => {
        if (!canInvite) return;

        const inviteId = String(invite.id || invite.email || '');
        const email = String(invite.email || '').trim();
        const dataRoleId = resolvePendingInviteDataRoleId(invite);
        if (!email) {
            addToast({
                title: t('website.membersSettings.invitationResendFailed'),
                description: t('website.membersSettings.failedToResendInvite'),
                type: 'error',
            });
            return;
        }

        setResendingInviteIds(prev => new Set(prev).add(inviteId));
        try {
            const workspaceRole = resolvePendingInviteWorkspaceRole(invite);
            await inviteMember(email, workspaceRole, dataRoleId);
            addToast({
                title: t('website.membersSettings.invitationResent'),
                description: t('website.membersSettings.invitationResentDescription', {
                    email,
                    workspace: currentWorkspace?.name,
                }),
                type: 'success',
                duration: MEMBER_INVITATION_SUCCESS_TOAST_DURATION_MS,
            });
        } catch (error: any) {
            addToast({
                title: t('website.membersSettings.invitationResendFailed'),
                description: error.message || t('website.membersSettings.failedToResendInvite'),
                type: 'error',
            });
        } finally {
            setResendingInviteIds(prev => {
                const next = new Set(prev);
                next.delete(inviteId);
                return next;
            });
        }
    };

    const handleCancelInvite = async (invite: any) => {
        if (!canInvite) return;

        const inviteId = String(invite.id || invite.email || '');
        const inviteToken = String(invite.token || '').trim();
        const email = String(invite.email || '').trim();

        if (!inviteToken) {
            addToast({
                title: t('website.membersSettings.invitationCancelFailed'),
                description: t('website.membersSettings.failedToCancelInvite'),
                type: 'error',
            });
            return;
        }

        setCancellingInviteIds(prev => new Set(prev).add(inviteId));
        try {
            await cancelInvite(inviteToken);
            addToast({
                title: t('website.membersSettings.invitationCancelled'),
                description: t('website.membersSettings.invitationCancelledDescription', { email }),
                type: 'success',
            });
        } catch (error: any) {
            addToast({
                title: t('website.membersSettings.invitationCancelFailed'),
                description: error.message || t('website.membersSettings.failedToCancelInvite'),
                type: 'error',
            });
        } finally {
            setCancellingInviteIds(prev => {
                const next = new Set(prev);
                next.delete(inviteId);
                return next;
            });
        }
    };

    const handleInviteButtonClick = () => {
        if (requiresInviteUpgrade) {
            setIsInviteUpgradeModalOpen(true);
            return;
        }

        if (hasReachedMemberLimit) {
            setIsInviteLimitModalOpen(true);
            return;
        }

        setIsInviteModalOpen(true);
    };

    const handleOpenInviteUpgradePricing = () => {
        setIsInviteUpgradeModalOpen(false);
        modal.openModal('pricing');
    };

    const handleOpenRolesUpgradePricing = () => {
        setIsRolesUpgradeModalOpen(false);
        modal.openModal('pricing');
    };

    const handleInviteLimitPrimaryAction = () => {
        setIsInviteLimitModalOpen(false);
        if (inviteLimitPrompt.primaryActionTarget === 'sales') {
            setIsInviteLimitContactSalesOpen(true);
            return;
        }
        modal.openModal('pricing');
    };

    const closeDeleteMemberModal = () => {
        if (isProcessing) {
            return;
        }

        setMemberToDelete(null);
    };

    const confirmDelete = async () => {
        if (!memberToDelete) return;

        setIsProcessing(true);
        try {
            // 使用 userId 作为删除成员的 ID
            const memberId = memberToDelete.userId || String(memberToDelete.id);
            console.log('删除成员 ID:', memberId);

            await removeMember(memberId);
            void integrations.refresh(false, { forceRefresh: true }).catch((error) => {
                console.warn('[MembersSettings] Failed to refresh integrations after member removal', error);
            });
            setMemberToDelete(null);
            addToast({
                title: t('website.membersSettings.userRemoved'),
                description: t('website.membersSettings.userRemovedDescription'),
                type: 'success'
            });
        } catch (error: any) {
            addToast({
                title: t('website.membersSettings.removalFailed'),
                description: error.message || t('website.membersSettings.failedToRemoveUser'),
                type: 'error'
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const assignRoleToMember = async (memberId: string, roleOption: RoleOption | string): Promise<string> => {
        const roleName = typeof roleOption === 'string' ? roleOption : roleOption.name;

        if (!currentWorkspace?.id) {
            throw new Error(t('website.membersSettings.workspaceUnavailable'));
        }

        const dataRole = roleOptions.find(role =>
            Number.isFinite(Number(role.id))
            && (
                normalizeRoleValue(role.name) === normalizeRoleValue(roleName)
                || String(role.id) === String(roleName)
            ));
        if (dataRole) {
            const assignment = await featureService.assignWorkspaceDataRole(
                currentWorkspace.id,
                memberId,
                { roleId: Number(dataRole.id) }
            );
            const confirmedRoleName = assignment.roleName || dataRole.name;
            setConfirmedDataRoleByMemberId((prev) => ({
                ...prev,
                [memberId]: confirmedRoleName,
            }));
            await refresh();
            await loadWorkspaceRoleOptions();
            return confirmedRoleName;
        }

        throw new Error(t('website.membersSettings.selectedRoleUnavailable'));
    };

    const handleUpdateRole = async (memberId: string, role: RoleOption | string, previousRole: string) => {
        const roleName = typeof role === 'string' ? role : role.name;
        const isCurrentUserMember = allMembers.some((member: any) =>
            getMemberRowId(member) === memberId && member.isCurrentUser);

        if (isCurrentUserMember) {
            addToast({
                title: t('website.membersSettings.updateFailed'),
                description: t('website.membersSettings.cannotChangeOwnRole'),
                type: 'error',
            });
            return;
        }

        let didSucceed = false;
        setUpdatingRoleMemberIds((prev) => {
            const next = new Set(prev);
            next.add(memberId);
            return next;
        });
        setOptimisticRoleByMemberId((prev) => ({
            ...prev,
            [memberId]: roleName,
        }));

        try {
            console.log('更新成员角色 ID:', memberId, '新角色:', roleName);

            const updatedRoleName = await assignRoleToMember(memberId, role);
            didSucceed = true;
            addToast({
                title: t('website.membersSettings.roleUpdated'),
                description: t('website.membersSettings.roleUpdatedDescription', {
                    role: updatedRoleName,
                }),
                type: 'success'
            });
        } catch (error: any) {
            setOptimisticRoleByMemberId((prev) => {
                const next = { ...prev };

                if (previousRole) {
                    next[memberId] = previousRole;
                } else {
                    delete next[memberId];
                }

                return next;
            });
            addToast({
                title: t('website.membersSettings.updateFailed'),
                description: error.message || t('website.membersSettings.failedToUpdateMemberRole'),
                type: 'error'
            });
        } finally {
            setUpdatingRoleMemberIds((prev) => {
                const next = new Set(prev);
                next.delete(memberId);
                return next;
            });

            if (didSucceed) {
                setOptimisticRoleByMemberId((prev) => {
                    const next = { ...prev };
                    delete next[memberId];
                    return next;
                });
            }
        }
    };

    const handleBatchReassignMembersRole = async (memberIds: string[], nextRole: string) => {
        if (memberIds.length === 0) {
            return;
        }

        setIsProcessing(true);
        try {
            for (const memberId of memberIds) {
                await assignRoleToMember(memberId, nextRole);
            }

            addToast({
                title: t('website.membersSettings.usersReassigned'),
                description: t('website.membersSettings.usersReassignedDescription', {
                    count: memberIds.length,
                    plural: memberIds.length > 1 ? 's' : '',
                    role: formatRoleLabel(nextRole, t),
                }),
                type: 'success',
            });
        } catch (error: any) {
            addToast({
                title: t('website.membersSettings.reassignmentFailed'),
                description: error.message || t('website.membersSettings.failedToUpdateMembers'),
                type: 'error',
            });
            throw error;
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSaveDataRole = async (role: Role) => {
        if (!currentWorkspace?.id || isSoloWorkspace) {
            throw new Error(t('website.membersSettings.rolePermissionsUnavailable'));
        }

        const systemPermissionGrants = buildWorkspaceSystemPermissionGrantInputs(role);

        try {
            const roleId = Number(role.id);
            const isExistingRole = Number.isFinite(roleId);
            let savedRole: WorkspaceDataRole;

            if (isExistingRole) {
                savedRole = await featureService.updateWorkspaceDataRole(currentWorkspace.id, Number(role.id), {
                    name: role.name.trim(),
                    description: role.description,
                    grants: systemPermissionGrants,
                });
            } else {
                savedRole = await featureService.createWorkspaceDataRole(currentWorkspace.id, {
                    name: role.name.trim(),
                    description: role.description,
                    grants: systemPermissionGrants,
                });
            }

            const savedUiRole: Role = {
                ...role,
                id: String(savedRole.id),
                name: savedRole.name || role.name.trim(),
                description: savedRole.description ?? role.description,
                roleKey: savedRole.roleKey,
                isSystem: Boolean(savedRole.systemRole) || savedRole.defaultRole || savedRole.roleType === 'SYSTEM_DEFAULT',
                isEditable: (savedRole.roleKey || '').toUpperCase() !== 'OWNER'
                    && (savedRole.defaultRole || savedRole.deletable !== false),
                isDeletable: Boolean(savedRole.deletable),
            };
            void loadWorkspaceDataRoles(true);
            void loadWorkspaceRoleOptions();
            await permissions.refresh();

            addToast({
                title: t('website.membersSettings.roleSaved'),
                description: t('website.membersSettings.roleSavedDescription'),
                type: 'success',
            });

            return savedUiRole;
        } catch (error: any) {
            addToast({
                title: t('website.membersSettings.saveFailed'),
                description: error.message || t('website.membersSettings.failedToSaveRolePermissions'),
                type: 'error',
            });
            throw error;
        }
    };

    const handleDeleteDataRole = async (role: Role) => {
        if (!currentWorkspace?.id || isSoloWorkspace || !Number.isFinite(Number(role.id))) {
            throw new Error(t('website.membersSettings.rolePermissionsUnavailable'));
        }

        try {
            await featureService.deleteWorkspaceDataRole(currentWorkspace.id, Number(role.id));
            void loadWorkspaceDataRoles(true);
            void loadWorkspaceRoleOptions();
            addToast({
                title: t('website.membersSettings.roleDeleted'),
                description: t('website.membersSettings.roleDeletedDescription'),
                type: 'success',
            });
        } catch (error: any) {
            addToast({
                title: t('website.membersSettings.deleteFailed'),
                description: error.message || t('website.membersSettings.failedToDeleteRole'),
                type: 'error',
            });
            throw error;
        }
    };

    const handlePrepareDataRole = useCallback(async (role: Role) => {
        if (!currentWorkspace?.id || isSoloWorkspace) {
            return role;
        }

        const roleId = Number(role.id);
        const grants = Number.isFinite(roleId)
            ? await featureService.getWorkspaceDataRoleGrants(currentWorkspace.id, roleId)
            : [];

        return applyWorkspaceSystemPermissionDetailsToRole(role, grants);
    }, [currentWorkspace?.id, isSoloWorkspace]);

    const closeInlineRoleDrawer = () => {
        inlineRoleDrawerRequestIdRef.current += 1;
        setIsInlineRolePreparing(false);
        setInlineEditingRole(undefined);
    };

    const openInlineNewRoleDrawer = canManageRoleAccess
        ? async () => {
            const requestId = inlineRoleDrawerRequestIdRef.current + 1;
            inlineRoleDrawerRequestIdRef.current = requestId;
            setIsInlineRolePreparing(true);
            setInlineEditingRole(inlineEmptyRole);

            try {
                const preparedRole = await handlePrepareDataRole(inlineEmptyRole);
                if (inlineRoleDrawerRequestIdRef.current !== requestId) {
                    return;
                }

                setInlineEditingRole(currentRole => (
                    currentRole?.id === inlineEmptyRole.id ? preparedRole : currentRole
                ));
            } catch (error: any) {
                if (inlineRoleDrawerRequestIdRef.current === requestId) {
                    setInlineEditingRole(undefined);
                    addToast({
                        title: t('website.membersSettings.roleAccessUnavailable'),
                        description: error.message || t('website.membersSettings.failedToLoadNewRoleAccess'),
                        type: 'error',
                    });
                }
            } finally {
                if (inlineRoleDrawerRequestIdRef.current === requestId) {
                    setIsInlineRolePreparing(false);
                }
            }
        }
        : undefined;

    const openTransferOwnershipModal = (targetMemberId: string) => {
        if (!canStartOwnerTransfer) {
            if (pendingOwnerTransfer) {
                addToast({
                    title: t('website.workspaceSettings.ownerTransferPendingTitle'),
                    description: t('website.workspaceSettings.ownerTransferPendingDescription', {
                        name: pendingOwnerTransfer.targetName || pendingOwnerTransfer.targetEmail || 'the selected user',
                    }),
                    type: 'info',
                });
            }
            return;
        }

        const matchedTarget = transferCandidates.find(member => member.userId === targetMemberId);
        if (!matchedTarget) {
            return;
        }

        setTransferTargetMemberId(matchedTarget.userId);
        setTransferTargetSnapshot(matchedTarget);
        setIsTransferOwnershipModalOpen(true);
    };

    const handleTransferOwnership = async () => {
        if (!canStartOwnerTransfer) {
            if (pendingOwnerTransfer) {
                addToast({
                    title: t('website.workspaceSettings.ownerTransferPendingTitle'),
                    description: t('website.workspaceSettings.ownerTransferPendingDescription', {
                        name: pendingOwnerTransfer.targetName || pendingOwnerTransfer.targetEmail || 'the selected user',
                    }),
                    type: 'info',
                });
            }
            return;
        }

        if (!selectedTransferTarget) {
            return;
        }

        const currentUserMemberId = currentUserMember ? getMemberRowId(currentUserMember) : null;

        if (!currentUserMemberId) {
            addToast({
                title: t('website.workspaceSettings.transferFailedTitle'),
                description: t('website.membersSettings.couldNotResolveCurrentOwner'),
                type: 'error',
            });
            return;
        }

        setIsTransferringOwnership(true);
        try {
            await transferOwnership(selectedTransferTarget.userId, 'TRANSFER');
            await refresh();
            setIsTransferOwnershipModalOpen(false);
            setTransferTargetMemberId('');
            setTransferTargetSnapshot(null);

            addToast({
                title: t('website.workspaceSettings.ownerTransferRequestSentTitle'),
                description: t('website.workspaceSettings.ownerTransferRequestSent', {
                    name: selectedTransferTarget.displayName,
                }),
                type: 'success',
            });
        } catch (error: any) {
            addToast({
                title: t('website.workspaceSettings.transferFailedTitle'),
                description: error.message || t('website.workspaceSettings.transferFailedFallback'),
                type: 'error',
            });
        } finally {
            setIsTransferringOwnership(false);
        }
    };

    const handleTabChange = (tab: 'members' | 'roles') => {
        if (tab === 'roles' && isSoloWorkspace) {
            setIsRolesUpgradeModalOpen(true);
            return;
        }

        setActiveTab(tab);
        if (tab === 'roles') {
            void loadWorkspaceDataRoles();
        }
        if (tab === 'members') {
            void loadWorkspaceRoleOptions();
        }
    };

    // 已迁移到 AuthContext utils 中的 getMemberAvatarUrl

    const getStatusBadge = (status: string) => {
        if (status === 'INVITED' || status === 'PENDING') {
            return (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700">
                    <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                    {t('website.membersSettings.statusPending')}
                </span>
            );
        }
        if (status === 'ACTIVE') {
            return (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    {t('website.membersSettings.statusActive')}
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700">
                {status}
            </span>
        );
    };

    return (
        <div className="flex flex-col space-y-6">
            {/* Tab Navigation */}
            <div className="flex gap-1 border-b border-[#E8EBF3]">
                {(['members', 'roles'] as const).map(tab => (
                    <button
                        key={tab}
                        onClick={() => handleTabChange(tab)}
                        className={clsx(
                            'relative pb-3 px-1 mr-5 text-sm font-medium transition-colors capitalize',
                            activeTab === tab ? 'text-[#1A162F]' : 'text-gray-400 hover:text-gray-700'
                        )}
                    >
                        {tab === 'members' ? t('website.membersSettings.accountsTab') : t('website.membersSettings.rolesTab')}
                        {activeTab === tab && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-[#1A162F]" />}
                    </button>
                ))}
            </div>

            {activeTab === 'roles' ? (
                <RolesTab
                    roles={roles}
                    onRolesChange={setRoles}
                    members={roleAssignableMembers}
                    onReassignMembersRole={handleBatchReassignMembersRole}
                    onSaveRole={handleSaveDataRole}
                    onDeleteRole={handleDeleteDataRole}
                    onPrepareRole={handlePrepareDataRole}
                    canManageRoles={canManageRoleAccess}
                    isLoading={isRolesTabLoading}
                />
            ) : (
                <div className="flex flex-col space-y-6">
                    {/* Toolbar */}
                    <div className="flex flex-wrap items-center justify-between gap-4 px-1">
                        <div className="flex flex-1 flex-wrap items-center gap-3">
                            <div className="relative flex-1 min-w-[260px] max-w-md">
                                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-sub" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder={t('website.membersSettings.searchUsers')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full rounded-[10px] border border-[#E8EBF3] bg-white py-2 pl-9 pr-10 text-sm font-normal text-[#1A162F] transition-all placeholder:text-[#8A93B2] focus:outline-none focus:border-[#1A162F] focus:ring-1 focus:ring-[#1A162F]"
                                />
                                {searchQuery.trim() ? (
                                    <button
                                        type="button"
                                        onClick={clearSearch}
	                                        aria-label={t('website.membersSettings.clearUserSearch')}
	                                        title={t('website.membersSettings.clearUserSearch')}
                                        className="absolute right-3 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-[#A0A8C0] transition-colors hover:text-[#1A162F]"
                                    >
                                        <XCircle size={16} weight="fill" />
                                    </button>
                                ) : null}
                            </div>
                            <RoleFilterDropdown
                                options={roleFilterOptions}
                                selectedValues={selectedRoleFilters}
                                onToggle={toggleRoleFilter}
                                onClear={() => setSelectedRoleFilters([])}
                            />
                        </div>
                        {canManageMembers ? (
                            <button
                                onClick={handleInviteButtonClick}
                                className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors shadow-sm shadow-primary/20"
                            >
                                <UserCirclePlus size={16} weight="regular" />
                                {t('website.membersSettings.addNewUser')}
                            </button>
                        ) : null}
                    </div>

                    {/* Members List */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                        <table className="min-w-[900px] w-full text-left border-collapse">
                            <thead className="bg-gray-50/50 border-b border-gray-100 sticky top-0 z-10">
                                <tr>
                                    <th className="px-4 py-3 text-xs font-medium text-text-sub w-[35%]">{t('website.membersSettings.user')}</th>
                                    <th className="px-4 py-3 text-xs font-medium text-text-sub text-left">{t('website.membersSettings.role')}</th>
                                    <th className="px-4 py-3 text-xs font-medium text-text-sub">{t('website.membersSettings.status')}</th>
                                    <th className="px-4 py-3 text-xs font-medium text-text-sub">{t('website.membersSettings.joinedDate')}</th>
                                    {canManageMembers ? (
                                        <th className="px-4 py-3 text-right text-xs font-medium text-text-sub">{t('website.membersSettings.actions')}</th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {shouldShowInitialSkeleton ? (
                                    <>
                                        <MemberRowSkeleton />
                                        <MemberRowSkeleton />
                                        <MemberRowSkeleton />
                                    </>
                                ) : !shouldShowLoadError ? (
                                    paginatedRows.map((row) => {
                                        const rowMemberId = String(row.userId || row.id);
                                        const rowRoleNormalized = normalizeRoleValue(String(row.role || ''));
                                        const isOwnerRow = row.type === 'member' && rowRoleNormalized === 'OWNER';
                                        const isRowUpdating = updatingRoleMemberIds.has(rowMemberId)
                                            || isTransferringOwnership;

                                        if (isRowUpdating) {
                                            return <MemberRowSkeleton key={row.id} />;
                                        }

                                        return (
                                            <tr key={row.id} className="group hover:bg-gray-50/50 transition-colors">
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <img
                                                            src={getMemberAvatarUrl(row)}
                                                            alt={row.type === 'member' ? row.displayName : row.email}
                                                            className="w-9 h-9 rounded-full border border-gray-200 bg-white"
                                                        />
                                                        <div className="min-w-0 max-w-[200px]">
                                                            <div className="font-medium text-text-main truncate">
                                                                {row.type === 'member' ? row.displayName : row.email}
                                                            </div>
                                                            {row.type === 'member' && (
                                                                <div className="text-sm text-text-sub truncate">
                                                                    {row.email}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {row.type === 'member' ? (
                                                        <RoleSelect
                                                            member={row}
                                                            onUpdateRole={(role) => {
                                                                handleUpdateRole(
                                                                    rowMemberId,
                                                                    role,
                                                                    String(row.role || '')
                                                                );
                                                            }}
                                                            canChangeRole={canChangeRole}
                                                            roles={memberAssignmentRoleOptions}
                                                            allowCustomRoles={!isSoloWorkspace}
                                                            isUpdating={isRowUpdating}
                                                            isLoadingRoles={isRoleOptionsLoading}
                                                            onNewRole={openInlineNewRoleDrawer}
                                                        />
                                                    ) : (
                                                        <span className="text-sm text-text-main">
                                                            {formatRoleLabel(row.role, t)}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {getStatusBadge(row.status)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-text-sub">
                                                    {row.type === 'member'
                                                        ? (row.joinedAt ? dayjs(row.joinedAt).format('MMM D, YYYY') : '-')
                                                        : (row.createdAt ? dayjs(row.createdAt).format('MMM D, YYYY') : '-')
                                                    }
                                                </td>
                                                {canManageMembers ? (
                                                    <td className="px-4 py-3 text-right">
                                                        {row.type === 'member' && !isOwnerRow ? (
                                                            <MemberActions
                                                                member={row}
                                                                onRemove={() => setMemberToDelete(row)}
                                                                canRemove={canRemove}
                                                                isDisabled={isRowUpdating}
                                                            />
                                                        ) : row.type === 'pending' ? (
                                                            <PendingInviteActions
                                                                onResend={() => handleResendInvite(row)}
                                                                onCancel={() => handleCancelInvite(row)}
                                                                canInvite={canInvite}
                                                                isResending={resendingInviteIds.has(rowMemberId)}
                                                                isCancelling={cancellingInviteIds.has(rowMemberId)}
                                                            />
                                                        ) : null}
                                                    </td>
                                                ) : null}
                                            </tr>
                                        );
                                    })
                                ) : null
                                }
                            </tbody>
                        </table>
                        </div>

                        {shouldShowLoadError ? (
                            <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
	                                <UsersThree size={48} weight="duotone" className="opacity-50 text-text-sub" />
	                                <div className="space-y-1">
	                                    <p className="text-sm font-medium text-text-main">{t('website.membersSettings.unableToLoadUsers')}</p>
	                                    <p className="text-sm text-text-sub">
	                                        {error?.message || t('website.membersSettings.tryAgainInMoment')}
	                                    </p>
	                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        void refresh();
                                    }}
	                                    className="inline-flex items-center rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC]"
	                                >
	                                    {t('website.membersSettings.retry')}
	                                </button>
                            </div>
                        ) : allRows.length === 0 && membersStatus !== 'loading' && (
                            <div className="flex flex-col items-center justify-center py-12 text-text-sub">
                                <User size={48} weight="duotone" className="mb-4 opacity-50" />
                                <p>
                                    {t('website.membersSettings.noUsersMatching', { query: searchQuery })}
                                </p>
                            </div>
                        )}
                        {totalMemberRows > 0 && !shouldShowInitialSkeleton && !shouldShowLoadError ? (
                            <PaginationFooter
                                ariaLabel={t('website.membersSettings.pagination')}
                                currentPage={membersPage}
                                getPageLabel={(page) => t('website.membersSettings.pageNumberLabel', { page })}
                                hasNextPage={membersPage < totalMemberPages}
                                nextPageLabel={t('website.membersSettings.nextPage')}
                                onPageChange={setMembersPage}
                                pageNumbers={visibleMemberPages}
                                previousPageLabel={t('website.membersSettings.previousPage')}
                                summary={t('website.membersSettings.showingUsersRange', {
                                    start: formatIntegerForDisplay(
                                        (membersPage - 1) * MEMBERS_ITEMS_PER_PAGE + 1,
                                        locale,
                                    ),
                                    end: formatIntegerForDisplay(
                                        Math.min(membersPage * MEMBERS_ITEMS_PER_PAGE, totalMemberRows),
                                        locale,
                                    ),
                                    total: formatIntegerForDisplay(totalMemberRows, locale),
                                })}
                            />
                        ) : null}
                    </div>
                </div>
            )}
            {/* end members tab */}

            {/* Invite Modal */}
            <Modal
                isOpen={isInviteModalOpen}
                onClose={() => {
                    if (isProcessing) return;
                    setIsInviteModalOpen(false);
                    setInviteEmail('');
                    setEmailError('');
                }}
                title={t('website.membersSettings.addNewUser')}
                width="max-w-md"
                bodyClassName="px-6 pb-6 pt-4"
                showHeaderDivider
            >
                <div className="space-y-4">
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-text-main">{t('website.membersSettings.emailAddress')}</label>
                        <input
                            type="email"
                            placeholder={t('website.membersSettings.inviteEmailPlaceholder')}
                            value={inviteEmail}
                            onChange={handleEmailChange}
                            disabled={isProcessing}
                            className={clsx(
                                "w-full rounded-[10px] border bg-white px-3 py-2.5 text-sm font-normal text-[#1A162F] transition-all placeholder:text-[#8A93B2] focus:outline-none focus:ring-1 focus:ring-[#1A162F] disabled:cursor-not-allowed disabled:bg-[#F8F9FC] disabled:opacity-50",
                                emailError
                                    ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                                    : "border-[#E8EBF3] focus:border-[#1A162F]"
                            )}
                            autoFocus
                        />
                        {emailError ? (
                            <p className="mt-1 text-sm text-red-600">
                                {emailError}
                            </p>
                        ) : null}
                    </div>
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-text-main">{t('website.membersSettings.role')}</label>
                        <InviteRoleDropdown
                            value={inviteRole}
                            onChange={setInviteRole}
                            roles={inviteRoleOptions}
                            disabled={isProcessing}
                            onNewRole={openInlineNewRoleDrawer}
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            onClick={() => setIsInviteModalOpen(false)}
                            disabled={isProcessing}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:opacity-50"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={handleInvite}
                            disabled={!inviteEmail.trim() || isProcessing}
                            className="flex items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-[#231D3E] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isProcessing ? (
                                <>
                                    <CircleNotch className="w-4 h-4 animate-spin" />
                                    <span>{t('website.membersSettings.addNewUser')}</span>
                                </>
                            ) : (
                                t('website.membersSettings.addNewUser')
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={!!memberToDelete}
                onClose={closeDeleteMemberModal}
                title={t('website.membersSettings.removeUser')}
                width="max-w-sm"
                bodyClassName="px-6 pb-6 pt-4"
                showHeaderDivider
            >
                <div className="space-y-4">
                    <p className="text-sm leading-6 text-[#666F8D]">
                        {t('website.membersSettings.removeUserConfirmation')}
                        {' '}
                        <span className="font-medium text-[#1A162F]">
                            {memberToDelete?.displayName}
                        </span>
                        {t('website.membersSettings.removeUserIrreversible')}
                    </p>
                    {ownedConnectionCountForDelete > 0 ? (
                        <div className="rounded-[10px] border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm leading-5 text-amber-900">
                            {t('website.membersSettings.integrationOwnerRemovalWarning', {
                                count: ownedConnectionCountForDelete,
                                plural: ownedConnectionCountForDelete === 1 ? '' : 's',
                            })}
                        </div>
                    ) : null}

                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            onClick={closeDeleteMemberModal}
                            disabled={isProcessing}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:opacity-50"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={confirmDelete}
                            disabled={isProcessing}
                            className="rounded-[10px] bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(220,38,38,0.12)] transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {t('website.membersSettings.remove')}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={isTransferOwnershipModalOpen}
                onClose={() => {
                    if (isTransferringOwnership) return;
                    setIsTransferOwnershipModalOpen(false);
                    setTransferTargetMemberId('');
                    setTransferTargetSnapshot(null);
                }}
                title={t('website.workspaceSettings.transferOwnership')}
                width="max-w-xl"
                bodyClassName="px-6 pb-6 pt-4"
                showHeaderDivider
            >
                <div className="space-y-4">
                    {selectedTransferTarget ? (
                        <div className="rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] px-3 py-3">
                            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-[#8A93B2]">
                                {t('website.membersSettings.newOwner')}
                            </p>
                            <p className="text-sm font-medium text-[#1A162F]">
                                {selectedTransferTarget.displayName}
                            </p>
                            <p className="mt-1 text-xs text-[#666F8D]">
                                {selectedTransferTarget.email}
                                {' · '}
                                {formatRoleLabel(selectedTransferTarget.role, t)}
                            </p>
                        </div>
                    ) : null}

                    <div className="rounded-xl border border-[#E8EBF3] bg-[#F8F9FC] px-3 py-3">
                        <p className="text-sm leading-relaxed text-[#666F8D]">
                            {selectedTransferTarget?.displayName || t('website.membersSettings.thisUser')}
                            {' '}
                            {t('website.membersSettings.transferOwnershipDescription')}
                            {' '}
                            <span className="font-medium text-[#1A162F]">{t('website.membersSettings.adminRole')}</span>
                            .
                        </p>
                    </div>

                    <div className="flex justify-end gap-3 pt-1">
                        <button
                            onClick={() => {
                                setIsTransferOwnershipModalOpen(false);
                                setTransferTargetMemberId('');
                                setTransferTargetSnapshot(null);
                            }}
                            disabled={isTransferringOwnership}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:opacity-50"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={() => {
                                void handleTransferOwnership();
                            }}
                            disabled={!canConfirmOwnershipTransfer || isTransferringOwnership}
                            className="flex items-center justify-center gap-2 rounded-[10px] bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(220,38,38,0.12)] transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isTransferringOwnership ? (
                                <>
                                    <CircleNotch className="w-4 h-4 animate-spin" />
                                    <span>{t('website.membersSettings.transferring')}</span>
                                </>
                            ) : (
                                t('website.membersSettings.transferOwnership')
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            <TeamUpgradeModal
                isOpen={isInviteUpgradeModalOpen}
                onClose={() => setIsInviteUpgradeModalOpen(false)}
                onPrimaryAction={handleOpenInviteUpgradePricing}
                prompt={inviteUpgradePrompt}
            />

            <TeamUpgradeModal
                isOpen={isRolesUpgradeModalOpen}
                onClose={() => setIsRolesUpgradeModalOpen(false)}
                onPrimaryAction={handleOpenRolesUpgradePricing}
                prompt={rolesUpgradePrompt}
            />

            <Modal
                isOpen={isInviteLimitModalOpen}
                onClose={() => setIsInviteLimitModalOpen(false)}
                title={inviteLimitPrompt.title}
                width="max-w-[520px]"
                containerClassName="overflow-hidden rounded-2xl border border-white/70 shadow-[0_24px_60px_rgba(26,22,47,0.18)]"
                headerClassName="px-6 py-5"
                bodyClassName="px-6 pb-6 pt-4"
                showHeaderDivider
            >
                <div>
                    <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-[18px] bg-[#1A162F] text-white shadow-[0_8px_20px_rgba(26,22,47,0.12)]">
                        <UsersThree size={26} weight="duotone" />
                    </div>

                    <div className="mx-auto max-w-[360px] text-center">
                        <p className="mx-auto text-sm font-normal leading-6 text-[#666F8D]">
                            {inviteLimitPrompt.description}
                        </p>
                    </div>

                    <div className="mt-6 flex items-center justify-center gap-3">
                        <button
                            onClick={() => setIsInviteLimitModalOpen(false)}
                            className="inline-flex items-center justify-center rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC]"
                        >
                            {inviteLimitPrompt.secondaryActionLabel}
                        </button>
                        <button
                            onClick={handleInviteLimitPrimaryAction}
                            className="inline-flex items-center gap-2 rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-[#231D3E]"
                        >
                            {inviteLimitPrompt.primaryActionLabel}
                            <ArrowRight size={16} weight="regular" />
                        </button>
                    </div>
                </div>
            </Modal>

            <ContactSalesModal
                isOpen={isInviteLimitContactSalesOpen}
                onClose={() => setIsInviteLimitContactSalesOpen(false)}
            />

            {inlineEditingRole !== undefined && canManageRoleAccess ? (
                <RoleDrawer
                    role={inlineEditingRole}
                    existingRoles={roles}
                    assignedMemberCount={0}
                    isPreparing={isInlineRolePreparing}
                    onClose={closeInlineRoleDrawer}
                    onSave={async (newRole) => {
                        const savedRole = await handleSaveDataRole(newRole);
                        setRoles(prev => {
                            const exists = prev.find(r => r.id === savedRole.id);
                            return exists ? prev.map(r => r.id === savedRole.id ? savedRole : r) : [...prev, savedRole];
                        });
                    }}
                />
            ) : null}


        </div>
    );
};

export default MembersSettings;
